import React, { useMemo } from 'react';
import moment from 'moment';
import { Form } from 'tea-component';
import TriggerViewCard from '@gp/components/TriggerViewCard';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import Table from '@gp/components/GeneralTable';
import RangePicker from '@gp/components/GeneralRangePicker';
import Filter from './components/Filter';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
// import { formatDateStr } from '@gp/utils/common';
import { getFirstDayOfMonth } from '@gp/utils/common';
import { FILE_TYPES2, FILE_TYPES_MAP } from '@gp/constants/const';
import { RANGE_PICKER_TYPE, RANGE_PICKER_TYPES } from '../../constants';

import type { SortBy } from 'tea-component/lib/table/addons';
import type { FilterItemType } from '@gp/components/GeneralSelectFilter';
import type { TableListOptionsType } from '@gp/components/GeneralTable/index.d';

import './index.scss';

interface ITimeRangeData {
  startDate: string;
  endDate: string;
}

interface IDatePickerData {
  dateRange: [string, string] | undefined;
  date: string;
}

interface LiveTitleCardPropsType extends IBaseProps {
  tableClassName: string;
  title: JSX.Element | null;
  filterLoading: boolean;
  filterTimeRangeData?: ITimeRangeData | null;
  filterOptions?: FilterItemType[];
  filterChange?: (studio: string[], project: string[], date: string, ym: string[], handleFilterName: string) => void;
  tableLoading: boolean;
  tableOptions: TableListOptionsType | null;
  tableRecords: any; // 数组每一项要有uniqueId
  onTableSort?: (v: SortBy<any>) => void;
  downloadParams: {
    select_id: string[];
    yoy: boolean;
    mom: boolean;
    order: 'desc' | 'asc' | undefined;
    order_metric: string;
  } | null;
  downloadServices: any;
  downloadLogKey?: {
    csv: string;
    excel: string;
  } | null;
  downloadDateValue?: [string, string];
  onDownloadDateChange?: (payload?: [string, string]) => void;
  onDownloadOpen?: () => void;
}

const DEFAULT_OPTIONS: FilterItemType[] = [];

const LiveTitleCard: React.FC<LiveTitleCardPropsType> = (props) => {
  const {
    tableClassName = '',
    title = null,
    filterLoading = true,
    filterTimeRangeData = null,
    filterOptions = DEFAULT_OPTIONS,
    filterChange,
    tableLoading = true,
    tableOptions = null,
    tableRecords = null,
    onTableSort,
    downloadParams = null,
    downloadServices = null,
    downloadLogKey = null,
    downloadDateValue,
    onDownloadDateChange,
    onDownloadOpen,
  } = props;

  const { isDownloading, handleDownload } = useDownload({
    csvDownloadLogKey: downloadLogKey?.csv || '',
    excelDownloadLogKey: downloadLogKey?.excel || '',
    downloadService: createDownloadService(downloadServices),
  });

  const { dateRange, date } = useMemo<IDatePickerData>(() => {
    if (filterLoading) return { dateRange: undefined, date: '' };
    const { startDate, endDate } = filterTimeRangeData || {};
    if (!startDate || !endDate) return { dateRange: undefined, date: '' };
    const startDateM = moment(startDate);
    const endDateM = moment(endDate);
    return {
      dateRange:
        !startDateM.isValid() || !endDateM.isValid()
          ? undefined
          : [startDateM.startOf('month').format('YYYY-MM-DD'), endDateM.endOf('month').format('YYYY-MM-DD')],
      date: getFirstDayOfMonth(endDate),
    };
  }, [filterLoading, filterTimeRangeData]);

  const tabs = useMemo(
    () => [
      {
        id: 'download',
        label: (
          <GeneralDownloadBtn
            uiType="2"
            fileTypes={FILE_TYPES2}
            isLoading={isDownloading}
            onOpen={onDownloadOpen}
            onConfirm={(payload) => {
              const fileType = payload?.fileType || FILE_TYPES_MAP.CSV;
              handleDownload({
                ...downloadParams,
                fileType,
                file_type: Number.parseInt(fileType, 10),
              });
            }}
          >
            <Form.Item className="dbg-live-title-download-duration-form-item" label="Duration">
              <RangePicker
                disabled={filterLoading}
                showShortcut={false}
                type={RANGE_PICKER_TYPE}
                typeList={RANGE_PICKER_TYPES}
                // @ts-ignore
                dateRange={dateRange}
                value={downloadDateValue}
                // @ts-ignore
                onChange={onDownloadDateChange}
              />
            </Form.Item>
          </GeneralDownloadBtn>
        ),
        component: (
          <section className="dbg-studio-monthly-report-live-title-card-body">
            <Filter
              loading={filterLoading}
              dateRange={dateRange}
              date={date}
              options={filterOptions}
              onChange={filterChange}
            />
            <Table
              className={tableClassName}
              loading={tableLoading}
              isTableBodyMaxH={false}
              tableOptions={tableOptions}
              tableRecords={tableRecords}
              onSort={onTableSort}
            />
          </section>
        ),
      },
    ],
    [tableClassName, tableLoading, filterLoading, isDownloading, downloadParams, handleDownload],
  );

  return (
    <TriggerViewCard
      className="dbg-studio-monthly-report-live-title-card"
      defaultActiveId="download"
      loading={false}
      isNoData={false}
      title={title}
      tabs={tabs}
    />
  );
};

export default React.memo(LiveTitleCard);
